/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../core'

import {KTSVG, detailPageUrl, DetailPage, ExpenseDetailPage} from '../../../helpers'
import {KTCard, KTCardBody, KTCardHeader, ChartColumn, Loading} from '../../../components'

import {Link} from 'react-router-dom'

import {useDispatch, useSelector} from 'react-redux'
import {cashFlowReportGetAll} from '../../../redux/slices/reporting.slice'

const selectReportingData = (state) => ({
  loading: state.reporting.loading,
  cashflow: state.reporting.cashflow
})

function Report() {
  const {loading, cashflow} = useSelector(selectReportingData)

  const intl = useIntl()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(cashFlowReportGetAll({}))
  }, [dispatch])

  const Widget = useMemo(() => {
    const WidgetComponent = ({
    className,
    color,
    svgIcon,
    iconColor,
    title,
    titleColor,
    description,
    descriptionColor,
  }) => {
    return (
      <div className={`card bg-${color} ${className}`}>
        <div className='card-body'>
          <KTSVG
            path={svgIcon}
            className={`svg-icon-${iconColor} svg-icon-5x ms-n1`}
            svgClassName={'mh-100px'}
          />

          <div className={`text-${titleColor} fw-bold fs-1 mb-2 mt-5`}>{title}</div>

          <div className={`fw-semibold text-${descriptionColor}`}>{description}</div>
        </div>
              </div>
      )
    }
    
    WidgetComponent.displayName = 'Widget'
    return WidgetComponent
  }, [])

  const formatCurrency = useCallback((value, currency = 'try') => {
    return intl.formatNumber(value, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })
  }, [intl])

  const formatDate = useCallback((date) => {
    return intl.formatDate(new Date(date), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }, [intl])

  const yearlyChartData = useMemo(() => {
    if (!cashflow || !cashflow.generalCashFlows) return []
    
    return [
      {
        name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANEXPENSES'}),
        data: cashflow.generalCashFlows.paymentPlansYearlyExpenses && cashflow.generalCashFlows.paymentPlansYearlyExpenses.map(
          (item) => item && item.totalPrice
        ),
      },
      {
        name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSALES'}),
        data: cashflow.generalCashFlows.paymentPlansYearlySales && cashflow.generalCashFlows.paymentPlansYearlySales.map(
          (item) => item && item.totalPrice
        ),
      },
      {
        name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSCASHFLOW'}),
        data: cashflow.generalCashFlows.paymentPlansYearlyCashFlows && cashflow.generalCashFlows.paymentPlansYearlyCashFlows.map(
          (item) => item && item.cashFlow
        ),
      },
    ]
  }, [cashflow, intl])

  const yearlyChartCategories = useMemo(() => {
    if (!cashflow || !cashflow.generalCashFlows || !cashflow.generalCashFlows.paymentPlansYearlyExpenses) return []
    
    return cashflow.generalCashFlows.paymentPlansYearlyExpenses.map(
      (item) => item && `${item.monthName}`
    )
  }, [cashflow])

  const monthlyChartData = useMemo(() => {
    if (!cashflow || !cashflow.currentCashFlows) return []
    
    return [
      {
        name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANEXPENSES'}),
        data: cashflow.currentCashFlows.paymentPlansMonthlyExpenses && cashflow.currentCashFlows.paymentPlansMonthlyExpenses.map(
          (item) => item && item.totalPrice
        ),
      },
      {
        name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSALES'}),
        data: cashflow.currentCashFlows.paymentPlansMonthlySales && cashflow.currentCashFlows.paymentPlansMonthlySales.map(
          (item) => item && item.totalPrice
        ),
      },
      {
        name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSCASHFLOW'}),
        data: cashflow.currentCashFlows.paymentPlansMonthlyCashFlows && cashflow.currentCashFlows.paymentPlansMonthlyCashFlows.map(
          (item) => item && item.cashFlow
        ),
      },
    ]
  }, [cashflow, intl])

  const monthlyChartCategories = useMemo(() => {
    if (!cashflow || !cashflow.currentCashFlows || !cashflow.currentCashFlows.paymentPlansMonthlyExpenses) return []
    
    return cashflow.currentCashFlows.paymentPlansMonthlyExpenses.map(
      (item) => item && `${item.day} ${item.monthName}`
    )
  }, [cashflow])

  const chartTooltip = useMemo(() => ({
    y: {
      formatter: (val) => {
        return formatCurrency(val)
      },
    },
  }), [formatCurrency])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.REPORTING.CASHFLOW'})}</PageTitle>

      <div className='row gy-5 g-xl-10'>
        <div className='col-xl-3'>
          <Widget
            className='card-xl-stretch mb-xl-8'
            svgIcon='/images/icons/duotune/finance/fin008.svg'
            color='primary'
            iconColor='white'
            title={formatCurrency(cashflow && cashflow.bankAccounts && cashflow.bankAccounts.totalDefaultCurrencyAmount)}
            description={intl.formatMessage({id: 'DASHBOARD.TOTALDEFAULTCURRENCYAMOUNT'})}
            titleColor='white'
            descriptionColor='white'
          />
        </div>
        <div className='col-xl-3'>
          <Widget
            className='card-xl-stretch mb-xl-8'
            svgIcon='/images/icons/duotune/finance/fin010.svg'
            color='info'
            iconColor='white'
            title={formatCurrency(cashflow && cashflow.bankAccounts && cashflow.bankAccounts.totalOtherCurrenciesAmount)}
            description={intl.formatMessage({id: 'DASHBOARD.TOTALOTHERCURRENCYAMOUNT'})}
            titleColor='white'
            descriptionColor='white'
          />
        </div>
        <div className='col-xl-3'>
          <Widget
            className='card-xl-stretch mb-xl-8'
            svgIcon='/images/icons/duotune/finance/fin001.svg'
            color='success'
            iconColor='white'
            title={formatCurrency(cashflow && cashflow.bankAccounts && cashflow.bankAccounts.totalAllCurrenciesAmount)}
            description={intl.formatMessage({id: 'DASHBOARD.TOTALALLCURRENCYAMOUNT'})}
            titleColor='white'
            descriptionColor='white'
          />
        </div>
        <div className='col-xl-3'>
          <div className='card card-xl-stretch mb-xl-10 bg-warning'>
            <div className='card-body d-flex flex-column'>
              <div className='d-flex flex-column mb-7'>
                <div className='text-white fw-bolder fs-3'>
                  {intl.formatMessage({id: 'REPORTING.CASHFLOW.BANKACCOUNTS.TITLE'})}
                </div>
              </div>

              <div className='row g-0'>
                {cashflow && cashflow.bankAccounts && cashflow.bankAccounts.currencyInfos && cashflow.bankAccounts.currencyInfos.map((item, index) => {
                  return (
                    <div className='col-6' key={index}>
                      <div className='d-flex align-items-center mb-9 me-2'>
                        <div>
                          <div className='fs-5 text-white fw-bolder lh-1'>
                            {formatCurrency(item && item.currencyRate, item && item.currencyCode)}
                          </div>
                          <div className='fs-7 text-white'>{item && item.name}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row gy-5 g-xl-10'>
        <div className='col-xl-8 mb-10'>
          <ChartColumn
            chartHeight='380px'
            categories={yearlyChartCategories}
            data={yearlyChartData}
            isMultipleColumn={true}
            title={intl.formatMessage({id: 'REPORTING.CASHFLOW.YEARLY.TITLE'})}
            tooltip={chartTooltip}
          />
        </div>

        <div className='col-xl-4'>
          <div className='row gy-5 g-xl-10 mb-10'>
            <div className='col-xl-12'>
              <KTCard>
                <KTCardHeader
                  title={intl.formatMessage({id: 'DASHBOARD.CASHFLOW.CURRENTBANKCASHACCOUNTS'})}
                />
                <KTCardBody>
                  <div className='card-scroll mh-350px'>
                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='fw-bold text-muted'>
                            <th className='min-w-150px'>
                              {intl.formatMessage({id: 'DASHBOARD.CASHFLOW.ACCOUNTNAME'})}
                            </th>
                            <th className='min-w-140px'>
                              {intl.formatMessage({id: 'DASHBOARD.CASHFLOW.TOTALPRICE'})}
                            </th>
                            <th className='min-w-140px'>
                              {intl.formatMessage({id: 'DASHBOARD.CASHFLOW.DEFAULTTOTALPRICE'})}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {cashflow && cashflow.bankAccounts && cashflow.bankAccounts.currenctBankCashAccounts && cashflow.bankAccounts.currenctBankCashAccounts.map((item, index) => {
                            return (
                              <tr key={index}>
                                <td>
                                  <div className='d-flex align-items-center'>
                                    <div className='d-flex justify-content-start flex-column'>
                                      {item.name}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className='text-dark fw-bold d-block fs-6'>
                                    {formatCurrency(item && item.totalAmount, item && item.currencyCode)}
                                    {item && item.currentRate != 0 && item && item.currencyCode != 'TRY' && (
                                      <>
                                        <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                          {formatCurrency(item && item.currentRate)}
                                        </span>
                                      </>
                                    )}
                                  </span>
                                </td>
                                <td>
                                  <span className='text-dark fw-bold d-block fs-6'>
                                    {formatCurrency(item && item.defaultCurrencyAmount)}
                                  </span>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>
          </div>
        </div>
      </div>

      <div className='row gy-5 g-xl-10'>
        <div className='col-xl-12 mb-10'>
          <ChartColumn
            chartHeight='380px'
            categories={monthlyChartCategories}
            data={monthlyChartData}
            isMultipleColumn={true}
            title={intl.formatMessage({id: 'REPORTING.CASHFLOW.DETAIL.TITLE'})}
            tooltip={chartTooltip}
          />
        </div>
      </div>

      <div className='row gy-5 g-xl-10 mb-10'>
        <div className='col-xl-6'>
          <KTCard>
            <KTCardHeader
              title={intl.formatMessage({id: 'REPORTING.CASHFLOW.PAYMENTPLANSEXPENSES.TITLE'})}
            />
            <KTCardBody>
              <div className='card-scroll mh-400px'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>
                          {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSEXPENSES.NAME'})}
                        </th>
                        <th className='min-w-150px'>
                          {intl.formatMessage({
                            id: 'DASHBOARD.PAYMENTPLANSEXPENSES.PAYMENTPLANTYPENAME',
                          })}{' '}
                          - {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSEXPENSES.COMPANYNAME'})}
                        </th>
                        <th className='min-w-140px'>
                          {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSEXPENSES.DEFERMENTDATE'})}
                        </th>
                        <th className='min-w-120px'>
                          {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSEXPENSES.TOTALPRICE'})}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {cashflow && cashflow.paymentPlansExpenses && cashflow.paymentPlansExpenses.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <div className='d-flex align-items-center'>
                                <div className='d-flex justify-content-start flex-column'>
                                  {item && item.expenseInvoiceId && (
                                    <>
                                      <Link
                                        to={detailPageUrl(
                                          ExpenseDetailPage[item.expenseTypeId],
                                          item.expenseInvoiceId
                                        )}
                                        className='text-dark fw-bold fs-6 text-hover-primary'
                                        target='_blank'
                                      >
                                        {item.description}
                                      </Link>
                                    </>
                                  )}
                                  {item && item.salaryId && (
                                    <>
                                      <Link
                                        to={detailPageUrl(DetailPage.Salary, item.salaryId)}
                                        className='text-dark fw-bold fs-6 text-hover-primary'
                                        target='_blank'
                                      >
                                        {item.description}
                                      </Link>
                                    </>
                                  )}
                                  {item && item.checkId && (
                                    <>
                                      <Link
                                        to={detailPageUrl(DetailPage.Check, item.checkId)}
                                        className='text-dark fw-bold fs-6 text-hover-primary'
                                        target='_blank'
                                      >
                                        {item.description}
                                      </Link>
                                    </>
                                  )}
                                  <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                    {item && item.name}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {item && item.paymentPlanTypeName}
                                {item && item.companyName && (
                                  <>
                                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                      {item && item.companyName}
                                    </span>
                                  </>
                                )}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatDate(item && item.defermenDate)}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.totalPrice)}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>

        <div className='col-xl-6'>
          <KTCard>
            <KTCardHeader
              title={intl.formatMessage({id: 'REPORTING.CASHFLOW.PAYMENTPLANSSALES.TITLE'})}
            />
            <KTCardBody>
              <div className='card-scroll mh-400px'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>
                          {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.NAME'})}
                        </th>
                        <th className='min-w-150px'>
                          {intl.formatMessage({
                            id: 'DASHBOARD.PAYMENTPLANSEXPENSES.PAYMENTPLANTYPENAME',
                          })}{' '}
                          - {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.COMPANYNAME'})}
                        </th>
                        <th className='min-w-140px'>
                          {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.DEFERMENTDATE'})}
                        </th>
                        <th className='min-w-120px'>
                          {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.TOTALPRICE'})}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {cashflow && cashflow.paymentPlansSales && cashflow.paymentPlansSales.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <div className='d-flex align-items-center'>
                                <div className='d-flex justify-content-start flex-column'>
                                  {item && item.saleInvoiceId && (
                                    <>
                                      <Link
                                        to={detailPageUrl(
                                          DetailPage.SaleInvoice,
                                          item.saleInvoiceId
                                        )}
                                        className='text-dark fw-bold fs-6 text-hover-primary'
                                        target='_blank'
                                      >
                                        {item.description}
                                      </Link>
                                    </>
                                  )}
                                  {item && item.checkId && (
                                    <>
                                      <Link
                                        to={detailPageUrl(DetailPage.Check, item.checkId)}
                                        className='text-dark fw-bold fs-6 text-hover-primary'
                                        target='_blank'
                                      >
                                        {item.description}
                                      </Link>
                                    </>
                                  )}
                                  <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                    {item && item.name}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {item && item.paymentPlanTypeName}
                                {item && item.companyName && (
                                  <>
                                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                      {item && item.companyName}
                                    </span>
                                  </>
                                )}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatDate(item && item.defermenDate)}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.totalPrice)}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>

        <div className='col-xl-6'>
          <KTCard>
            <KTCardHeader title={intl.formatMessage({id: 'REPORTING.CASHFLOW.YEARLY'})} />
            <KTCardBody>
              <div className='card-scroll mh-400px'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.NAME'})}
                        </th>
                        <th className='min-w-150px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.CASHFLOW'})}
                        </th>
                        <th className='min-w-140px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.EXPENSE'})}
                        </th>
                        <th className='min-w-120px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.SALE'})}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {cashflow && cashflow.generalCashFlowList && cashflow.generalCashFlowList.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>{item && item.name}</span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.yearlyCashFlowListItem)}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.yearlyExpenseListItem)}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.yearlySaleListItem)}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>

        <div className='col-xl-6'>
          <KTCard>
            <KTCardHeader title={intl.formatMessage({id: 'REPORTING.CASHFLOW.MONTHLY'})} />
            <KTCardBody>
              <div className='card-scroll mh-400px'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.NAME'})}
                        </th>
                        <th className='min-w-150px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.CASHFLOW'})}
                        </th>
                        <th className='min-w-140px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.EXPENSE'})}
                        </th>
                        <th className='min-w-120px'>
                          {intl.formatMessage({id: 'REPORTING.CASHFLOW.SALE'})}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {cashflow && cashflow.currentCashFlowList && cashflow.currentCashFlowList.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>{item && item.name}</span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.monthlyCashFlowListItem)}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.monthlyExpenseListItem)}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bold d-block fs-6'>
                                {formatCurrency(item && item.monthlySaleListItem)}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Report}
